import {
  useGetBackListeElettorali,
  type MembroListaElettorale,
} from '@framework/api-client';
import {
  ConfigFormItem,
  getWidgetProps,
  InputMarginContainer,
  RichTextAdvancedRender,
  useWidgetEnabled,
  useWidgetScopeContext,
  useWidgetVisibility,
  type IWidgetComponentProps,
} from '@framework/engine';
import { LoaderSpin } from '@framework/ui';
import { Col, Row } from 'antd';
import { useMemo } from 'react';
import { useController } from 'react-hook-form';
import { CandidateWidgetContainer } from '../candidate/CandidateWidgetContainer';
import { ElectoralListChooserListBox } from './components/ElectoralListChooserListBox';
import {
  electoralListChooserCandidateFieldName,
  electoralListChooserColumnsFieldName,
  electoralListChooserListNameFieldName,
} from './createElectoralListChooserFormSchema';
import { electoralListChooserFetchLimit } from './electoralListChooserMembers';
import type { IElectoralListChooserWidget } from './IElectoralListChooserWidget';

export interface IElectoralListChooserWidgetComponentProps
  extends IWidgetComponentProps<IElectoralListChooserWidget> {}

/**
 * Widget di scelta lista elettorale: l'utente seleziona una lista ed
 * eventualmente un candidato. L'invio del voto è demandato al pulsante di voto
 * (VoteWidget) presente nel form, come per le elezioni CTS.
 */
export function ElectoralListChooserWidgetComponent(
  props: Readonly<IElectoralListChooserWidgetComponentProps>,
) {
  const { widget } = props;
  const options = getWidgetProps(props);
  const visible = useWidgetVisibility(widget, options);
  const enabled = useWidgetEnabled(widget, options);
  const { prefix } = useWidgetScopeContext();

  // Campo principale (lista selezionata) e sotto-campo candidato, entrambi
  // pilotati via useController. `options.name` è già comprensivo del prefisso di
  // scope, così il binding regge anche con il widget annidato in sottosezioni.
  const { field: listField } = useController({ name: options.name });
  const candidateFieldName = `${prefix}${electoralListChooserCandidateFieldName(
    widget.code,
  )}`;
  const { field: candidateField } = useController({ name: candidateFieldName });
  // Oggetto con i campi del candidato scelto (nome, cognome, …), salvato per
  // esporli alle espressioni come riferimenti, analogamente al widget CTS.
  const columnsFieldName = `${prefix}${electoralListChooserColumnsFieldName(
    widget.code,
  )}`;
  const { field: columnsField } = useController({ name: columnsFieldName });
  // Nome della lista selezionata, salvato per esporlo alle espressioni come
  // riferimento (#57760).
  const listNameFieldName = `${prefix}${electoralListChooserListNameFieldName(
    widget.code,
  )}`;
  const { field: listNameField } = useController({ name: listNameFieldName });

  const selectedListId: string | undefined = listField.value;
  const selectedMemberId: string | undefined = candidateField.value;
  const ids = widget.electoralListIds ?? [];

  // Nomi delle liste recuperati una sola volta qui e passati ai box, invece di
  // rifare la stessa fetch in ogni box.
  const { data: lists, isLoading: listsLoading } = useGetBackListeElettorali({
    isAttiva: true,
    isTornataAttiva: true,
    limite: electoralListChooserFetchLimit,
  });
  const listNameById = useMemo(() => {
    const map: Record<string, string> = {};
    lists?.dati?.forEach(l => {
      if (l.id) map[l.id] = l.nome ?? '';
    });
    return map;
  }, [lists]);

  // Cliccare la lista già selezionata la deseleziona (azzera lista e candidato);
  // selezionarla imposta lista + candidato di default (primo per `ordine`).
  // Scriviamo sia l'id del candidato (per il voto) sia l'oggetto con i suoi
  // campi (per i riferimenti delle espressioni).
  const selectList = (listId: string, defaultMember?: MembroListaElettorale) => {
    const deselect = selectedListId === listId;
    listField.onChange(deselect ? null : listId);
    candidateField.onChange(deselect ? null : defaultMember?.id ?? null);
    columnsField.onChange(deselect ? null : defaultMember ?? null);
    listNameField.onChange(deselect ? null : listNameById[listId] ?? null);
  };

  const selectMember = (listId: string, member: MembroListaElettorale) => {
    listField.onChange(listId);
    candidateField.onChange(member.id ?? null);
    columnsField.onChange(member);
    listNameField.onChange(listNameById[listId] ?? null);
  };

  if (!visible || props.readonly) return null;

  return (
    <CandidateWidgetContainer
      panelTitle={widget.panelTitle}
      showPanel={widget.showPanel}
    >
      <InputMarginContainer>
        <ConfigFormItem
          name={options.name}
          label={<RichTextAdvancedRender inline text={options.label} />}
          help={<RichTextAdvancedRender inline text={options.helpText} />}
          layout={{ hideVisualLabel: options.hideLabel }}
          readonly={options.readonly}
        >
          {listsLoading ? (
            <LoaderSpin transparent />
          ) : (
            <Row gutter={[16, 16]}>
              {ids.map(listId => (
                <Col xs={24} md={12} key={listId}>
                  <ElectoralListChooserListBox
                    listId={listId}
                    listName={listNameById[listId] ?? ''}
                    selected={selectedListId === listId}
                    selectedMemberId={
                      selectedListId === listId ? selectedMemberId : undefined
                    }
                    columns={widget.columns}
                    disabled={enabled === false}
                    onSelectList={selectList}
                    onSelectMember={selectMember}
                  />
                </Col>
              ))}
            </Row>
          )}
        </ConfigFormItem>
      </InputMarginContainer>
    </CandidateWidgetContainer>
  );
}
